/**
 * useDictation — browser speech-to-text for the chat composer (#902).
 *
 * Speech becomes *editable text* in the composer: the transcript is handed to
 * the consumer via `onFinalResult` and never sent on its own. There is no
 * backend leg — this is the Web Speech API (`SpeechRecognition`, prefixed
 * `webkitSpeechRecognition` on Chrome/Safari) only.
 *
 * Honesty rule (root CLAUDE.md): where the API is missing, `supported` is false
 * and the consumer hides the mic entirely rather than rendering a dead button.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useMsg, type TKey } from '@/i18n/msg';

// The Web Speech API is not in lib.dom.d.ts (only the Result/Alternative
// sub-interfaces are), so the surface we actually use is typed here.
interface SpeechRecognitionAlternativeLike {
  transcript: string;
}

interface SpeechRecognitionResultLike {
  isFinal: boolean;
  length: number;
  [index: number]: SpeechRecognitionAlternativeLike;
}

interface SpeechRecognitionResultEvent {
  resultIndex: number;
  results: {
    length: number;
    [index: number]: SpeechRecognitionResultLike;
  };
}

interface SpeechRecognitionErrorEventLike {
  error: string;
  message?: string;
}

interface SpeechRecognitionLike {
  lang: string;
  continuous: boolean;
  interimResults: boolean;
  start: () => void;
  stop: () => void;
  abort: () => void;
  onresult: ((event: SpeechRecognitionResultEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEventLike) => void) | null;
  onend: (() => void) | null;
}

type SpeechRecognitionCtor = new () => SpeechRecognitionLike;

declare global {
  interface Window {
    SpeechRecognition?: SpeechRecognitionCtor;
    webkitSpeechRecognition?: SpeechRecognitionCtor;
  }
}

function getRecognitionCtor(): SpeechRecognitionCtor | null {
  if (typeof window === 'undefined') return null;
  return window.SpeechRecognition ?? window.webkitSpeechRecognition ?? null;
}

/**
 * Error copy is user-facing and must say what happened AND what to do — a bare
 * error code in the composer is worse than no message at all.
 */
function messageKeyForError(code: string): TKey | null {
  switch (code) {
    case 'not-allowed':
    case 'service-not-allowed':
      return 'chat:dictation.micBlocked';
    case 'no-speech':
      return 'chat:dictation.noSpeech';
    case 'audio-capture':
      return 'chat:dictation.noMic';
    case 'network':
      return 'chat:dictation.network';
    case 'aborted':
      return null;
    default:
      return 'chat:dictation.stopped';
  }
}

export interface UseDictationOptions {
  /** Receives each finalized transcript chunk. The consumer appends it to the composer. */
  onFinalResult: (text: string) => void;
  /** BCP-47 tag; defaults to the browser's language. */
  lang?: string;
}

export interface UseDictationResult {
  /** False when the browser has no SpeechRecognition — consumers hide the mic. */
  supported: boolean;
  recording: boolean;
  /** Begin listening. No-op when unsupported or already recording. */
  start: () => void;
  /** Stop listening and keep everything transcribed so far. */
  stop: () => void;
  /** Stop listening and discard the in-flight transcript. */
  cancel: () => void;
  /** User-facing error copy, or null. Cleared on the next start(). */
  error: string | null;
}

export function useDictation({ onFinalResult, lang }: UseDictationOptions): UseDictationResult {
  const [supported] = useState<boolean>(() => getRecognitionCtor() !== null);
  const [recording, setRecording] = useState(false);
  // #2109 R3 L5b: the error is held as a key and resolved at read, so it is
  // in the reader's language even across a language switch.
  const resolve = useMsg();
  const [errorKey, setError] = useState<TKey | null>(null);
  const error = errorKey ? resolve({ key: errorKey }) : null;

  const recognitionRef = useRef<SpeechRecognitionLike | null>(null);
  const cancelledRef = useRef(false);

  // Held in a ref so a re-created callback never re-binds the live session's
  // handlers mid-dictation (and never leaks a stale composer setter).
  const onFinalResultRef = useRef(onFinalResult);
  useEffect(() => {
    onFinalResultRef.current = onFinalResult;
  }, [onFinalResult]);

  const teardown = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    recognition.onresult = null;
    recognition.onerror = null;
    recognition.onend = null;
    recognitionRef.current = null;
  }, []);

  const start = useCallback(() => {
    if (recognitionRef.current) return;
    const Ctor = getRecognitionCtor();
    if (!Ctor) return;

    setError(null);
    cancelledRef.current = false;

    let recognition: SpeechRecognitionLike;
    try {
      recognition = new Ctor();
    } catch {
      setError('chat:dictation.unsupportedBrowser');
      return;
    }

    recognition.lang = lang ?? (typeof navigator !== 'undefined' ? navigator.language : 'en-US');
    recognition.continuous = true;
    recognition.interimResults = false;

    recognition.onresult = (event) => {
      if (cancelledRef.current) return;
      let finalText = '';
      for (let i = event.resultIndex; i < event.results.length; i += 1) {
        const result = event.results[i];
        if (result?.isFinal) finalText += result[0]?.transcript ?? '';
      }
      const trimmed = finalText.trim();
      if (trimmed) onFinalResultRef.current(trimmed);
    };

    recognition.onerror = (event) => {
      const key = messageKeyForError(event.error);
      if (key) setError(key);
      setRecording(false);
    };

    recognition.onend = () => {
      setRecording(false);
      teardown();
    };

    try {
      recognition.start();
    } catch {
      setError('chat:dictation.startFailed');
      return;
    }

    recognitionRef.current = recognition;
    setRecording(true);
  }, [lang, teardown]);

  const stop = useCallback(() => {
    const recognition = recognitionRef.current;
    if (!recognition) return;
    // stop() (unlike abort()) lets the engine flush its pending final result,
    // so the last thing the user said still lands in the composer.
    recognition.stop();
    setRecording(false);
  }, []);

  const cancel = useCallback(() => {
    const recognition = recognitionRef.current;
    cancelledRef.current = true;
    setError(null);
    setRecording(false);
    if (!recognition) return;
    recognition.abort();
  }, []);

  useEffect(() => {
    return () => {
      const recognition = recognitionRef.current;
      if (!recognition) return;
      cancelledRef.current = true;
      recognition.onresult = null;
      recognition.onerror = null;
      recognition.onend = null;
      recognition.abort();
      recognitionRef.current = null;
    };
  }, []);

  return { supported, recording, start, stop, cancel, error };
}

export default useDictation;
